import { useEffect, useRef, useState, useCallback } from 'react'
import type { IChartApi, ISeriesApi } from 'lightweight-charts'
import type { DrawingTool, Drawing, DrawingPoint } from '../../types'
import { useChartStore } from '../../store/chartStore'

interface DrawingLayerProps {
  chart: IChartApi | null
  mainSeries: ISeriesApi<any> | null
  activeTool: DrawingTool
}

export function DrawingLayer({ chart, mainSeries, activeTool }: DrawingLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { drawings, addDrawing, setActiveDrawingTool } = useChartStore()
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentPoints, setCurrentPoints] = useState<DrawingPoint[]>([])
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null)

  // Convert pixel to price/time
  const pixelToCoordinate = useCallback((x: number, y: number): DrawingPoint | null => {
    if (!chart || !mainSeries) return null
    
    const timeScale = chart.timeScale()
    const time = timeScale.coordinateToTime(x)
    const price = mainSeries.coordinateToPrice(y)
    
    if (time === null || price === null) return null
    
    return { time: time as number, price }
  }, [chart, mainSeries])

  // Convert price/time to pixel
  const coordinateToPixel = useCallback((point: DrawingPoint): { x: number; y: number } | null => {
    if (!chart || !mainSeries) return null
    
    const timeScale = chart.timeScale()
    const x = timeScale.timeToCoordinate(point.time as any)
    const y = mainSeries.priceToCoordinate(point.price)
    
    if (x === null || y === null) return null
    
    return { x, y }
  }, [chart, mainSeries])

  // Draw all drawings on canvas
  const renderDrawings = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !chart) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Draw existing drawings
    drawings.forEach(drawing => {
      if (!drawing.visible) return
      drawShape(ctx, drawing)
    })
    
    // Draw current drawing in progress
    if (isDrawing && currentPoints.length > 0) {
      const tempDrawing: Drawing = {
        id: 'temp',
        type: activeTool,
        points: [...currentPoints],
        style: {
          color: '#00d4ff',
          lineWidth: 2,
          lineStyle: 'solid',
        },
        visible: true,
        locked: false,
      }
      
      // Add mouse position for preview
      if (mousePosition && needsSecondPoint(activeTool)) {
        const coord = pixelToCoordinate(mousePosition.x, mousePosition.y)
        if (coord) {
          tempDrawing.points.push(coord)
        }
      }
      
      drawShape(ctx, tempDrawing)
    }
  }, [drawings, isDrawing, currentPoints, mousePosition, activeTool, chart, pixelToCoordinate])

  const drawShape = (ctx: CanvasRenderingContext2D, drawing: Drawing) => {
    const points = drawing.points.map(coordinateToPixel).filter(Boolean) as { x: number; y: number }[]
    if (points.length === 0) return

    ctx.strokeStyle = drawing.style.color
    ctx.lineWidth = drawing.style.lineWidth
    ctx.setLineDash(drawing.style.lineStyle === 'dashed' ? [5, 5] : drawing.style.lineStyle === 'dotted' ? [2, 2] : [])
    
    ctx.beginPath()
    
    switch (drawing.type) {
      case 'trendline':
      case 'ray':
      case 'extendedLine':
        if (points.length >= 2) {
          ctx.moveTo(points[0].x, points[0].y)
          ctx.lineTo(points[1].x, points[1].y)
          
          // Extend for ray/extendedLine
          if (drawing.type === 'ray' || drawing.type === 'extendedLine') {
            const dx = points[1].x - points[0].x
            const dy = points[1].y - points[0].y
            const len = Math.sqrt(dx * dx + dy * dy)
            const extendX = points[1].x + (dx / len) * 2000
            const extendY = points[1].y + (dy / len) * 2000
            ctx.lineTo(extendX, extendY)
          }
          
          if (drawing.type === 'extendedLine') {
            ctx.moveTo(points[0].x, points[0].y)
            const dx = points[0].x - points[1].x
            const dy = points[0].y - points[1].y
            const len = Math.sqrt(dx * dx + dy * dy)
            const extendX = points[0].x + (dx / len) * 2000
            const extendY = points[0].y + (dy / len) * 2000
            ctx.lineTo(extendX, extendY)
          }
        }
        break
        
      case 'horizontalLine':
        if (points.length >= 1) {
          ctx.moveTo(0, points[0].y)
          ctx.lineTo(ctx.canvas.width, points[0].y)
        }
        break
        
      case 'verticalLine':
        if (points.length >= 1) {
          ctx.moveTo(points[0].x, 0)
          ctx.lineTo(points[0].x, ctx.canvas.height)
        }
        break
        
      case 'rectangle':
        if (points.length >= 2) {
          const x = Math.min(points[0].x, points[1].x)
          const y = Math.min(points[0].y, points[1].y)
          const w = Math.abs(points[1].x - points[0].x)
          const h = Math.abs(points[1].y - points[0].y)
          ctx.rect(x, y, w, h)
          
          if (drawing.style.fillColor) {
            ctx.fillStyle = drawing.style.fillColor
            ctx.globalAlpha = drawing.style.fillOpacity || 0.2
            ctx.fill()
            ctx.globalAlpha = 1
          }
        }
        break
        
      case 'fibonacciRetracement':
        if (points.length >= 2) {
          const levels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1]
          const priceRange = points[1].y - points[0].y
          
          levels.forEach((level, i) => {
            const y = points[0].y + priceRange * level
            ctx.moveTo(0, y)
            ctx.lineTo(ctx.canvas.width, y)
            
            // Draw level text
            ctx.font = '10px sans-serif'
            ctx.fillStyle = drawing.style.color
            ctx.fillText(`${(level * 100).toFixed(1)}%`, 10, y - 5)
          })
        }
        break
        
      case 'parallelChannel':
        if (points.length >= 3) {
          // Main trendline
          ctx.moveTo(points[0].x, points[0].y)
          ctx.lineTo(points[1].x, points[1].y)
          
          // Parallel line
          const dx = points[1].x - points[0].x
          const dy = points[1].y - points[0].y
          const offsetY = points[2].y - points[0].y
          
          ctx.moveTo(points[0].x, points[0].y + offsetY)
          ctx.lineTo(points[1].x, points[1].y + offsetY)
        }
        break
    }
    
    ctx.stroke()
  }

  const needsSecondPoint = (tool: DrawingTool): boolean => {
    return ['trendline', 'ray', 'extendedLine', 'rectangle', 'fibonacciRetracement', 'parallelChannel'].includes(tool)
  }

  const getRequiredPoints = (tool: DrawingTool): number => {
    switch (tool) {
      case 'horizontalLine':
      case 'verticalLine':
        return 1
      case 'trendline':
      case 'ray':
      case 'extendedLine':
      case 'rectangle':
      case 'fibonacciRetracement':
        return 2
      case 'parallelChannel':
      case 'pitchfork':
        return 3
      default:
        return 2
    }
  }

  // Mouse handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (activeTool === 'cursor' || activeTool === 'crosshair') return
    
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const coord = pixelToCoordinate(x, y)
    
    if (!coord) return
    
    if (!isDrawing) {
      setIsDrawing(true)
      setCurrentPoints([coord])
    } else {
      const newPoints = [...currentPoints, coord]
      setCurrentPoints(newPoints)
      
      if (newPoints.length >= getRequiredPoints(activeTool)) {
        // Complete the drawing
        addDrawing({
          type: activeTool,
          points: newPoints,
          style: {
            color: '#00d4ff',
            lineWidth: 2,
            lineStyle: 'solid',
          },
          visible: true,
          locked: false,
        })
        setIsDrawing(false)
        setCurrentPoints([])
        setActiveDrawingTool('cursor')
      }
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }

  // Resize canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const resize = () => {
      canvas.width = canvas.parentElement?.clientWidth || 0
      canvas.height = canvas.parentElement?.clientHeight || 0
      renderDrawings()
    }
    
    resize()
    window.addEventListener('resize', resize)
    
    // Re-render on chart scroll/zoom
    if (chart) {
      chart.timeScale().subscribeVisibleTimeRangeChange(renderDrawings)
    }
    
    return () => {
      window.removeEventListener('resize', resize)
    }
  }, [chart, renderDrawings])

  // Render drawings when data changes
  useEffect(() => {
    renderDrawings()
  }, [renderDrawings])

  if (activeTool === 'cursor') {
    return null
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-auto z-20"
      style={{ cursor: 'crosshair' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
    />
  )
}
